<template>
  <SoAdminPage
    title="Sync email template"
    :back-to="{ name: RouteNames.AdminEmailTemplates }"
  >
    <SoDialog
      :model-value="true"
      :card-title="`Sync from ${payload?.sourceEnvironment ?? 'another environment'}`"
      :max-width="560"
      persistent
      data-testid="sync-import-dialog"
    >
      <div
        v-if="loading"
        class="d-flex align-center ga-3 mb-2"
        data-testid="sync-import-loading"
      >
        <SoProgress indeterminate :size="24" />
        <span class="text-medium-emphasis">Reading the synced template…</span>
      </div>
      <SoAlert
        v-else-if="problem"
        type="error"
        class="mb-4"
        :text="problem"
        data-testid="sync-import-error"
      />
      <template v-else-if="payload && communication">
        <p class="mb-4">
          This creates version {{ version }} of
          <strong>{{ communication.name }}</strong> on {{ environment }} and
          {{
            payload.makeLive
              ? "publishes it, replacing the current live version."
              : "saves it as a draft."
          }}
        </p>
        <div class="mb-4">
          <div class="text-caption text-medium-emphasis">Subject</div>
          <div data-testid="sync-import-subject">{{ payload.subject }}</div>
        </div>
        <SoCheckbox
          v-if="isProductionSync"
          v-model="customerImpactAcknowledged"
          label="I understand this will have an immediate effect on communications sent to our customers"
          data-testid="sync-import-customer-impact"
        />
        <SoAlert
          v-if="saveFailed"
          type="error"
          class="mb-4"
          text="Failed to create the email template. Please try again."
          data-testid="sync-import-save-error"
        />
      </template>

      <div class="d-flex justify-end ga-2 mt-2">
        <SoButton
          title="Cancel"
          :disabled="saving"
          data-testid="sync-import-cancel"
          @click="cancel"
        />
        <SoButton
          v-if="canSave"
          :title="confirmTitle"
          :type="isProductionSync ? 'danger' : 'primary'"
          :loading="saving"
          :disabled="isProductionSync && !customerImpactAcknowledged"
          data-testid="sync-import-confirm"
          @click="save"
        />
      </div>
    </SoDialog>
  </SoAdminPage>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import { RouteNames } from "@/types/router"
  import { EmailTemplatesService } from "@/services/admin/EmailTemplatesService"
  import { EmailTemplatesMutator } from "@/mutators/emailTemplates/EmailTemplatesMutator"
  import { AuthService } from "@/services/AuthService"
  import { useSnackbarStore } from "@/stores/snackbar"
  import SoAdminPage from "@/components/templates/SoAdminPage.vue"
  import SoDialog from "@/components/SoDialog.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoAlert from "@/components/SoAlert.vue"
  import SoProgress from "@/components/SoProgress.vue"
  import SoCheckbox from "@/components/formControls/SoCheckbox.vue"
  import { QueryResult } from "@/api/utils/types"
  import {
    ICommunication,
    IEmailTemplate,
    IEmailTemplateSyncPayload,
    SyncEnvironment,
  } from "@/models/admin/emailTemplates/emailTemplates.types"
  import { nextVersion } from "./helpers/emailTemplateRows"
  import {
    currentSyncEnvironment,
    decodeSyncPayload,
    syncedModifiedReason,
  } from "./helpers/emailTemplateSync"

  export default defineComponent({
    components: {
      SoAdminPage,
      SoDialog,
      SoButton,
      SoAlert,
      SoProgress,
      SoCheckbox,
    },
    data() {
      return {
        RouteNames,
        environment: currentSyncEnvironment(),
        snackbar: useSnackbarStore(),
        communicationsQuery: EmailTemplatesService.getCommunications(),
        versionsQuery: null as QueryResult<IEmailTemplate[]> | null,
        createMutator: EmailTemplatesMutator.createTemplate(),
        createLiveMutator: EmailTemplatesMutator.createTemplateAsLive(),
        payload: null as IEmailTemplateSyncPayload | null,
        decoding: true,
        saving: false,
        saveFailed: false,
        customerImpactAcknowledged: false,
      }
    },
    computed: {
      communicationsReady(): boolean {
        return (
          this.communicationsQuery.isFetched &&
          !this.communicationsQuery.isPlaceholderData
        )
      },
      communication(): ICommunication | null {
        if (!this.payload) return null
        return (
          (this.communicationsQuery.data ?? []).find(
            (communication) =>
              communication.name === this.payload?.communicationName,
          ) ?? null
        )
      },
      versionsReady(): boolean {
        return (
          !!this.versionsQuery &&
          this.versionsQuery.isFetched &&
          !this.versionsQuery.isPlaceholderData
        )
      },
      loading(): boolean {
        if (this.decoding) return true
        if (!this.payload) return false
        if (!this.communicationsReady) return true
        return !!this.communication && !this.versionsReady
      },
      problem(): string | null {
        if (!this.payload) {
          return "This sync link is invalid or incomplete. Open it again from the other environment."
        }
        if (this.payload.sourceEnvironment === this.environment) {
          return `This sync link was created on ${this.environment}. Open it on the other environment.`
        }
        if (!this.communication) {
          return `Channel "${this.payload.communicationName}" doesn't exist on ${this.environment}. Create the channel here first, then sync again.`
        }
        return null
      },
      version(): number {
        if (!this.communication) return 1
        return nextVersion(
          this.versionsQuery?.data ?? [],
          this.communication.id,
        )
      },
      confirmTitle(): string {
        const action = this.payload?.makeLive ? "Publish" : "Save"
        return `${action} to ${this.isProductionSync ? "prod" : "staging"}`
      },
      isProductionSync(): boolean {
        return this.environment === SyncEnvironment.PRODUCTION
      },
      canSave(): boolean {
        return !this.loading && !this.problem
      },
    },
    watch: {
      // Keyed on the id: a communications refetch yields new objects for the
      // same channel, which must not restart the versions query.
      "communication.id": {
        immediate: true,
        handler(communicationId: number | undefined) {
          this.versionsQuery = communicationId
            ? EmailTemplatesService.getEmailTemplatesByCommunication(
                communicationId,
              )
            : null
        },
      },
    },
    async created() {
      this.payload = await decodeSyncPayload(this.$route.hash.slice(1))
      this.decoding = false
    },
    methods: {
      cancel() {
        this.$router.push({ name: RouteNames.AdminEmailTemplates })
      },
      async save() {
        const { payload, communication } = this
        if (!payload || !communication || !this.canSave) return
        if (this.isProductionSync && !this.customerImpactAcknowledged) return
        this.saving = true
        this.saveFailed = false
        const currentUser = AuthService.getCurrentUser()
        // Captured up front: the mutation invalidates the versions query, which
        // would bump the computed number before the redirect reads it.
        const version = this.version
        try {
          const mutator = payload.makeLive
            ? this.createLiveMutator
            : this.createMutator
          await mutator.mutateAsync({
            communicationId: communication.id,
            version,
            isLive: payload.makeLive,
            subject: payload.subject,
            htmlContent: payload.htmlContent,
            plainContent: payload.plainContent,
            modifiedBy: currentUser.name || currentUser.username || null,
            modifiedReason: syncedModifiedReason(payload),
          })
          this.snackbar.addSuccess(
            payload.makeLive
              ? "Email template published"
              : "Email template saved",
          )
          this.$router.replace({
            name: RouteNames.AdminEmailTemplatesDetail,
            params: { communicationId: String(communication.id) },
            query: { version: String(version) },
          })
        } catch {
          this.saveFailed = true
          // A version created concurrently would collide on the same number.
          this.versionsQuery?.refetch()
        } finally {
          this.saving = false
        }
      },
    },
  })
</script>
